import { useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { typeLabels } from "@/lib/labels";

interface SearchBarProps {
  initialValue?: string;
  variant?: "navbar" | "hero";
  placeholder?: string;
}

export default function SearchBar({
  initialValue = "",
  variant = "navbar",
  placeholder = "搜索电影、电视剧、综艺或资讯...",
}: SearchBarProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef<number | null>(null);

  const suggestions = useMemo(() => {
    const kw = value.trim().toLowerCase();
    if (!kw) return [];
    return mediaList
      .filter((m) =>
        [m.title, m.originalTitle, ...m.genres, ...m.tags].join(" ").toLowerCase().includes(kw),
      )
      .slice(0, 6);
  }, [value]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = value.trim();
    if (!kw) return;
    setFocused(false);
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  const goToSuggestion = (id: string, name: string) => {
    setValue(name);
    setFocused(false);
    navigate(`/search?q=${encodeURIComponent(name)}`);
    void id;
  };

  const isHero = variant === "hero";

  return (
    <div className="relative w-full">
      <form onSubmit={submit} className="w-full">
        <div
          className={`flex items-center gap-2 rounded-full border transition-colors ${
            isHero
              ? "h-12 md:h-14 bg-background-100/90 border-background-300/70 px-4 md:px-5"
              : "h-10 bg-background-100 border-background-300/60 px-4"
          }`}
        >
          <i className={`ri-search-line text-foreground-300 ${isHero ? "text-lg" : "text-base"}`}></i>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => setFocused(false), 150);
            }}
            placeholder={placeholder}
            className={`flex-1 bg-transparent text-foreground-950 placeholder:text-foreground-300 focus:outline-none ${
              isHero ? "text-sm md:text-base" : "text-sm"
            }`}
          />
          {value && (
            <button
              type="button"
              onClick={() => setValue("")}
              className="w-6 h-6 flex items-center justify-center text-foreground-300 hover:text-foreground-950 cursor-pointer"
              aria-label="清空"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
          <button
            type="submit"
            className={`flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap ${
              isHero ? "h-8 md:h-9 px-4 text-sm" : "h-7 px-3 text-xs"
            }`}
          >
            搜索
          </button>
        </div>
      </form>

      {focused && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 overflow-hidden rounded-xl border border-background-300/60 bg-background-100/98 backdrop-blur-md animate-fade-in">
          <p className="px-4 pt-3 pb-1 text-xs text-foreground-300">联想结果</p>
          <ul>
            {suggestions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    goToSuggestion(s.id, s.title);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-background-200/70 cursor-pointer"
                >
                  <i className="ri-play-circle-line text-accent-500 text-base"></i>
                  <span className="flex-1 text-sm text-foreground-900 truncate">{s.title}</span>
                  <span className="text-xs text-foreground-300 whitespace-nowrap">
                    {typeLabels[s.type]} · {s.year}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}